import { Fragment, createElement, useEffect, useState } from 'react';
import { notification } from 'antd';

import { Pane, Table, Button, Input } from 'components/common';
import { useGet, usePost } from 'hooks';
import { userKeywords } from 'services/api';
import { format } from 'utils/date';

import { modals } from './Modals';
import {
  showWarning,
  Alert,
  errors,
  validationErrors,
  getFirstApiValidationError,
} from './helpers';
import useLog from 'hooks/useLog';
import { KeywordPaneHeader } from './KeywordPaneHeader';

const MAX_KEYWORDS = 150;
export const KEYWORD_MAX_LENGTH = 50;

const Keywords = () => {
  const [{ res, loading }, getKeywords] = useGet({
    url: userKeywords,
    onMount: true,
  });
  const [
    { res: addRes, loading: addding, error },
    postKeyword,
    setRes,
    setErr,
  ] = usePost({
    url: userKeywords,
  });
  const [keywordFilter, setKeywordFilter] = useState('');
  const [activeModal, setActiveModal] = useState();
  const [activeKeyword, setActiveKeyword] = useState();
  const [validationError, setValidationError] = useState(null);

  const validKeyword = keywordFilter.trim().length > 0;
  const hasExceededKeywordLimit = res?.length >= MAX_KEYWORDS;

  const keywordFilterPlaceholder = hasExceededKeywordLimit
    ? 'Type to filter...'
    : 'Add New/Filter';

  const isAddButtonDisabled =
    loading ||
    addding ||
    !keywordFilter ||
    !validKeyword ||
    hasExceededKeywordLimit ||
    !!validationError;

  useLog('User clicked to Keyword Management.', 'ViewKeywords', 'Success');

  useEffect(() => {
    if (error) {
      const description = getFirstApiValidationError(error);
      notification.open({
        message: 'Cannot process',
        description: (
          <>
            <div>{description}</div>
          </>
        ),
        placement: 'bottomLeft',
      });
    }
  }, [error]);

  useEffect(() => {
    if (addRes) {
      notification.open({
        message: 'Keyword Added',
        description: (
          <div>
            Successfully added <b>"{keywordFilter}"</b> to your list of
            keywords.
          </div>
        ),
        placement: 'bottomLeft',
      });
      setRes();
      setKeywordFilter('');
      getKeywords();
    }
  }, [addRes, getKeywords, keywordFilter, setRes]);

  const getFilteredData = () => {
    if (!res) return [];
    if (!keywordFilter || !validKeyword) return res;

    return res.filter(k =>
      `${k.keyword.toLowerCase()}`.includes(keywordFilter.toLowerCase())
    );
  };

  const handleAdd = () => {
    if (hasExceededKeywordLimit) {
      return;
    }

    postKeyword({ keyword: keywordFilter.trim() });
  };

  const handleKeywordChange = value => {
    if (error) {
      setErr();
    }

    // Filter out invalid characters for both typing and pasting
    const sanitizedValue = value.replace(/[^a-z0-9\-+ *]/gi, '');
    setKeywordFilter(sanitizedValue);

    if (sanitizedValue.length > KEYWORD_MAX_LENGTH) {
      setValidationError(validationErrors.MAX_KEYWORD_LENGTH);
    } else if (validationError) {
      setValidationError(null);
    }
  };

  const handleKeyDown = e => {
    // Remove character validation from here since it's now handled in handleKeywordChange
    if (e.keyCode === 13 && keywordFilter) {
      if (!hasExceededKeywordLimit) {
        handleAdd();
      }
    }
  };

  const handleEdit = keyword => {
    setActiveKeyword(keyword);
    setActiveModal('edit');
  };

  const handleDelete = keyword => {
    setActiveKeyword(keyword);
    setActiveModal('delete');
  };

  const handleCloseModal = () => {
    setActiveModal();
    setActiveKeyword();
  };

  const handleModalSuccess = () => {
    handleCloseModal();
    getKeywords();
  };

  const config = {
    headers: [
      { label: 'Keyword', key: 'keyword' },
      { label: 'Last Modified By', key: 'operator' },
      { label: 'Last Modified On', key: 'operationTime' },
      '',
    ],
    createRow: (r, i) => (
      <Fragment key={i}>
        <div>{r.keyword}</div>
        <div>{r.operator ? `${r.operator} (${r.operation})` : '-'}</div>
        <div>{format(r.operationTime)}</div>
        <div style={{ justifyContent: 'flex-end' }}>
          <Button
            size='small'
            onClick={() => handleEdit(r)}
            style={{ marginRight: 10 }}
          >
            Edit
          </Button>
          <Button size='small' onClick={() => handleDelete(r)}>
            Delete
          </Button>
        </div>
      </Fragment>
    ),
  };

  return (
    <Pane
      title='Keyword Management'
      headerRight={
        <KeywordPaneHeader
          keywordCount={res?.length}
          maxKeywordCount={MAX_KEYWORDS}
        />
      }
    >
      <div>
        <div style={{ display: 'flex', marginTop: 15 }}>
          <Input
            disabled={loading || addding}
            onChange={handleKeywordChange}
            onKeyDown={handleKeyDown}
            value={keywordFilter}
            placeholder={keywordFilterPlaceholder}
            style={{ marginRight: 15 }}
          />
          <Button
            disabled={isAddButtonDisabled}
            type='primary'
            onClick={handleAdd}
          >
            Add
          </Button>
        </div>
        {errors[validationError]}
        {showWarning(keywordFilter) && <Alert />}
        <Table
          config={config}
          data={getFilteredData()}
          loading={loading}
          style={{ marginTop: 15 }}
          paginate
          sortable
        />
      </div>
      {activeModal &&
        createElement(modals[activeModal], {
          activeKeyword,
          onCloseModal: handleCloseModal,
          onSuccess: handleModalSuccess,
        })}
    </Pane>
  );
};

export default Keywords;
